import {
    ArrowLeft,
    BookMarked,
    Clock,
    MessagesSquare,
    User,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { getSession, getSessionMessages } from '~/api/sessions'
import type { AdminSessionDetail, TranscriptMessage } from '~/api/types'
import { MessageList } from '~/components/Transcript'
import { StatCard } from '~/components/StatCard'
import { Badge, type BadgeTone } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Card } from '~/components/ui/card'
import { EmptyState } from '~/components/ui/empty-state'
import { Skeleton } from '~/components/ui/skeleton'
import { useToast } from '~/components/ui/toast'
import { formatDateTime, formatDuration, sessionUserLabel } from '~/lib/utils'

// 세션 상세 페이지(/sessions/:id). 제목 + 분석 패널(StatCard) + 전체 대화 transcript.
// GET /sessions/:id (상세 지표) + /sessions/:id/messages (말풍선)를 함께 조회한다.
const STATUS_TONES: Record<string, BadgeTone> = {
    active: 'info',
    completed: 'success',
    archived: 'neutral',
}

export function SessionDetailPage() {
    const { id = '' } = useParams()
    // ?focus=<messageId> — 피드백 드릴다운에서 평가된 답변을 강조·스크롤한다.
    const [searchParams] = useSearchParams()
    const focusId = searchParams.get('focus') ?? undefined
    const navigate = useNavigate()
    const toast = useToast()
    const [session, setSession] = useState<AdminSessionDetail | null>(null)
    const [messages, setMessages] = useState<TranscriptMessage[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (!id) return
        let alive = true
        setLoading(true)
        Promise.all([getSession(id), getSessionMessages(id)])
            .then(([detail, msgs]) => {
                if (!alive) return
                setSession(detail)
                setMessages(msgs)
            })
            .catch((e) => {
                if (alive) toast.error((e as Error).message)
            })
            .finally(() => {
                if (alive) setLoading(false)
            })
        return () => {
            alive = false
        }
    }, [id, toast])

    const messageCount = session?.messageCount ?? messages.length

    return (
        <div className='space-y-5'>
            <div className='flex items-center gap-2'>
                <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => navigate(-1)}
                    aria-label='뒤로가기'>
                    <ArrowLeft />
                    뒤로
                </Button>
            </div>

            {loading ? (
                <DetailSkeleton />
            ) : !session ? (
                <EmptyState
                    icon={MessagesSquare}
                    title='세션을 찾을 수 없어요.'
                    description='삭제되었거나 접근 권한이 없는 세션이에요.'
                    action={
                        <Button
                            variant='outline'
                            size='sm'
                            onClick={() => navigate(-1)}>
                            뒤로가기
                        </Button>
                    }
                />
            ) : (
                <>
                    <header>
                        <div className='flex flex-wrap items-center gap-2'>
                            <h1 className='text-foreground text-lg font-semibold tracking-tight'>
                                {session.title || '제목 없음'}
                            </h1>
                            <Badge
                                tone={
                                    STATUS_TONES[session.status] ?? 'neutral'
                                }>
                                {session.status}
                            </Badge>
                        </div>
                        <p className='text-muted-foreground mt-0.5 text-[13px]'>
                            {sessionUserLabel(session)} ·{' '}
                            {formatDateTime(session.createdAt)}
                        </p>
                    </header>

                    <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
                        <StatCard
                            label='메시지 수'
                            value={messageCount}
                            icon={MessagesSquare}
                            tone='primary'
                        />
                        <StatCard
                            label='참조 수'
                            value={session.citationCount ?? '-'}
                            icon={BookMarked}
                            tone='info'
                        />
                        <StatCard
                            label='소요시간'
                            value={formatDuration(session.durationSeconds)}
                            icon={Clock}
                        />
                        <StatCard
                            label='사용자'
                            value={sessionUserLabel(session)}
                            hint={session.userEmail ?? undefined}
                            icon={User}
                            tone='success'
                        />
                    </div>

                    <Card className='p-5'>
                        <h3 className='krds-title text-foreground mb-1'>
                            세션 정보
                        </h3>
                        <dl className='mt-3 grid grid-cols-1 gap-x-6 gap-y-2 text-[13px] sm:grid-cols-2'>
                            <Field label='이메일' value={session.userEmail} />
                            <Field label='이름' value={session.userName} />
                            <Field label='프로필' value={session.profile} />
                            <Field
                                label='사용자 메시지'
                                value={
                                    session.userCount != null
                                        ? String(session.userCount)
                                        : null
                                }
                            />
                            <Field
                                label='어시스턴트 메시지'
                                value={
                                    session.assistantCount != null
                                        ? String(session.assistantCount)
                                        : null
                                }
                            />
                            <Field
                                label='업데이트'
                                value={formatDateTime(session.updatedAt)}
                            />
                        </dl>
                    </Card>

                    <Card className='p-5'>
                        <h3 className='krds-title text-foreground mb-3'>
                            대화 내용
                        </h3>
                        {messages.length === 0 ? (
                            <p className='text-muted-foreground text-sm'>
                                메시지가 없어요.
                            </p>
                        ) : (
                            <MessageList
                                messages={messages}
                                highlightId={focusId}
                            />
                        )}
                    </Card>
                </>
            )}
        </div>
    )
}

function Field({
    label,
    value,
}: {
    label: string
    value: string | null | undefined
}) {
    return (
        <div className='flex justify-between gap-3'>
            <dt className='text-muted-foreground shrink-0'>{label}</dt>
            <dd className='text-foreground min-w-0 truncate text-right'>
                {value || '-'}
            </dd>
        </div>
    )
}

function DetailSkeleton() {
    return (
        <div className='space-y-5'>
            <Skeleton className='h-7 w-64' />
            <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
                {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className='h-20' />
                ))}
            </div>
            <Skeleton className='h-40' />
            <Skeleton className='h-64' />
        </div>
    )
}
